/**
 * Items by Hour — WHEN does each item perform best?
 *
 * Three viewing modes:
 *   • All Days       — aggregates every date in the global window
 *   • Single Day     — filters to a single weekday (e.g. only Sundays)
 *   • Compare Days   — two-arm side-by-side split (e.g. Sunday vs Thursday)
 *                      with per-day normalisation and a local date preset.
 *
 * Golden rule: By default we respect the global date range. Compare Mode is
 * the ONLY scenario that may override the window, because cross-weekday
 * analysis needs multiple occurrences of each weekday.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Clock, Lightning, Flame, Sun, ArrowsLeftRight,
  Warning, Equals, TrendUp, Sparkle, Info,
} from '@phosphor-icons/react';
import { salesApi } from '../../lib/api';
import { toast } from 'sonner';
import DeltaPill from '../../components/DeltaPill';

// ──────────────────────────────────────────────────────────────────
// Design tokens
// ──────────────────────────────────────────────────────────────────

const SEVERITY_COLOR = {
  low:    { text: 'text-zinc-400',    bg: 'bg-zinc-500/10',    border: 'border-zinc-500/30' },
  medium: { text: 'text-amber-300',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30' },
  high:   { text: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
};

const INSIGHT_ICON = {
  time_concentrated:  Flame,
  all_day:            Sun,
  opportunity:        Lightning,
  compare_disparity:  ArrowsLeftRight,
  compare_consistent: Equals,
  time_specific_peak: TrendUp,
};

const WEEKDAYS = [
  { idx: 0, short: 'Mon', long: 'Monday' },
  { idx: 1, short: 'Tue', long: 'Tuesday' },
  { idx: 2, short: 'Wed', long: 'Wednesday' },
  { idx: 3, short: 'Thu', long: 'Thursday' },
  { idx: 4, short: 'Fri', long: 'Friday' },
  { idx: 5, short: 'Sat', long: 'Saturday' },
  { idx: 6, short: 'Sun', long: 'Sunday' },
];

const COMPARE_RANGES = [7, 14, 30, 60, 90];

// Linear intensity scale.
const heatColor = (val, maxVal) => {
  if (!maxVal || !val) return 'rgba(255,255,255,0.03)';
  const t = Math.min(1, val / maxVal);
  const r = Math.round(39 + (16  - 39) * t);
  const g = Math.round(39 + (185 - 39) * t);
  const b = Math.round(42 + (129 - 42) * t);
  return `rgba(${r}, ${g}, ${b}, ${0.25 + 0.55 * t})`;
};

const textColor = (val, maxVal) => {
  if (!maxVal || !val) return 'text-zinc-600';
  return (val / maxVal) > 0.55 ? 'text-zinc-900 font-medium' : 'text-zinc-200';
};

const fmtCell = (useAvg, c) => {
  if (useAvg) {
    const v = c.qty_per_day;
    return v == null ? '·' : (v >= 10 ? Math.round(v) : v.toFixed(1));
  }
  return c.qty || '·';
};

// ──────────────────────────────────────────────────────────────────
// Risk flag badges — inline indicators that make demand reliability
// legible *inside* the heatmap row, so operators spot risk where the
// decision happens (no new page).
// ──────────────────────────────────────────────────────────────────

const FLAG_META = {
  new_item: {
    Icon: Sparkle,
    color: 'text-teal-300',
    bg: 'bg-teal-500/10',
    border: 'border-teal-500/30',
    label: 'New',
    tip: 'New item — first seen under 14 days ago. Use manual judgment until history accumulates.',
    priority: 1,
  },
  high_variance: {
    Icon: Warning,
    color: 'text-amber-300',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    label: 'Volatile',
    tip: 'High variance — daily demand swings >40%. Be cautious with large prep or stock commits.',
    priority: 2,
  },
  low_data: {
    Icon: Info,
    color: 'text-zinc-300',
    bg: 'bg-zinc-500/10',
    border: 'border-zinc-500/30',
    label: 'Low data',
    tip: 'Fewer than 4 matching days in this window. Widen the range before trusting this signal.',
    priority: 3,
  },
};

const orderFlags = (flags = []) =>
  [...flags].sort((a, b) => (FLAG_META[a]?.priority || 99) - (FLAG_META[b]?.priority || 99));

const RiskBadges = ({ stats, compact = false }) => {
  const flags = orderFlags(stats?.risk_flags || []);
  if (!flags.length) return null;
  const tooltip = flags.map((f) => `${FLAG_META[f]?.label || f}: ${FLAG_META[f]?.tip || ''}`).join('\n');
  return (
    <span className="inline-flex items-center gap-1 ml-1.5 align-middle" title={tooltip} data-testid="risk-badges">
      {flags.map((f) => {
        const m = FLAG_META[f];
        if (!m) return null;
        const Icon = m.Icon;
        return compact ? (
          <Icon
            key={f}
            size={11}
            weight={f === 'new_item' ? 'fill' : 'regular'}
            className={m.color}
            data-testid={`risk-flag-${f}`}
          />
        ) : (
          <span
            key={f}
            className={`inline-flex items-center gap-0.5 px-1 py-[1px] rounded-sm text-[9px] font-mono uppercase tracking-wider border ${m.bg} ${m.border} ${m.color}`}
            data-testid={`risk-flag-${f}`}
          >
            <Icon size={9} weight={f === 'new_item' ? 'fill' : 'regular'} />
            {m.label}
          </span>
        );
      })}
    </span>
  );
};

const hasAnyFlag = (it) => (it?.stats?.risk_flags || []).length > 0;

// Date helpers from shared Date Policy module — see /lib/date.js.
import { fmtLocalISO, todayISO as todayIso, isoMinusDays } from '../../lib/date';
const daysAgoIso = (n) => isoMinusDays(n - 1);

// ──────────────────────────────────────────────────────────────────
// Heatmap (single arm)
// ──────────────────────────────────────────────────────────────────

const Heatmap = ({
  buckets, items, maxVal, useAvg = false,
  hoveredBucket, onHoverBucket, label, testId = 'items-by-hour-heatmap',
  periodComparison = null,
}) => {
  if (!items?.length) {
    return (
      <div className="rounded-xl border border-white/5 bg-[#0F1115] p-6 text-center text-sm text-zinc-500 font-mono" data-testid={`${testId}-empty`}>
        No item data for {label || 'this selection'}. Try widening the window.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/5 bg-[#0F1115] overflow-hidden" data-testid={testId}>
      {label && (
        <div className="px-3 py-2 border-b border-white/5 text-[10px] uppercase tracking-widest font-mono text-zinc-400 flex items-center justify-between">
          <span>{label}</span>
          <span className="text-zinc-600">{useAvg ? 'avg / day' : 'total qty'}</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-[#0F1115] text-left text-[10px] uppercase tracking-widest font-mono text-zinc-500 px-3 py-2 w-[220px] min-w-[220px]">
                Item
              </th>
              {buckets.map((b) => (
                <th
                  key={b.label}
                  onMouseEnter={() => onHoverBucket && onHoverBucket(b.label)}
                  onMouseLeave={() => onHoverBucket && onHoverBucket(null)}
                  className={`text-center text-[10px] uppercase tracking-widest font-mono px-2 py-2 min-w-[64px] transition-colors ${
                    hoveredBucket === b.label ? 'bg-white/5 text-zinc-100' : 'text-zinc-500'
                  }`}
                >
                  <div>{b.label}</div>
                  <div className="text-[9px] text-zinc-600 mt-0.5">{b.title}</div>
                </th>
              ))}
              <th className="text-right text-[10px] uppercase tracking-widest font-mono text-zinc-500 px-3 py-2">
                {useAvg ? 'avg/d' : 'Total'}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr
                key={`${it.item_id}-${idx}`}
                className="border-t border-white/[0.03] hover:bg-white/[0.02]"
                data-testid={`${testId}-row-${idx}`}
              >
                <td className="sticky left-0 z-10 bg-[#0F1115] px-3 py-2 text-zinc-100">
                  <div className="flex items-center min-w-0">
                    <span className="truncate max-w-[160px]" title={it.item_name}>{it.item_name}</span>
                    <RiskBadges stats={it.stats} compact />
                  </div>
                  {it.category && (
                    <div className="text-[10px] text-zinc-600 font-mono">{it.category}</div>
                  )}
                </td>
                {it.cells.map((c) => {
                  const v = useAvg ? (c.qty_per_day ?? 0) : c.qty;
                  return (
                    <td
                      key={c.label}
                      style={{ backgroundColor: heatColor(v, maxVal) }}
                      title={`${it.item_name} · ${c.label} · ${c.qty} units${c.qty_per_day != null ? ` · ${c.qty_per_day}/day` : ''} · ₹${Math.round(c.revenue).toLocaleString('en-IN')}`}
                      className={`text-center font-mono tabular-nums px-2 py-2 ${textColor(v, maxVal)} ${
                        hoveredBucket === c.label ? 'ring-1 ring-white/20' : ''
                      }`}
                      data-testid={`${testId}-cell-${idx}-${c.label}`}
                    >
                      {fmtCell(useAvg, c)}
                    </td>
                  );
                })}
                <td className="text-right px-3 py-2 font-mono tabular-nums text-zinc-300">
                  <div className="inline-flex items-center gap-1.5 justify-end">
                    <span>
                      {useAvg
                        ? (it.avg_qty_per_day != null ? (it.avg_qty_per_day >= 10 ? Math.round(it.avg_qty_per_day) : it.avg_qty_per_day.toFixed(1)) : '·')
                        : it.total_qty}
                    </span>
                    {periodComparison && it.quantity_change_direction && (
                      <DeltaPill
                        direction={it.quantity_change_direction}
                        pct={it.quantity_change_pct}
                        title={`${periodComparison.label || 'vs prev'} · prev qty ${it.previous_quantity ?? '—'}`}
                        size="xs"
                        testId={`${testId}-delta-${idx}`}
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────
// Top items per bucket (single-arm only)
// ──────────────────────────────────────────────────────────────────

const TopPerBucket = ({ buckets, topPerBucket, hoveredBucket, onHoverBucket }) => (
  <div className="rounded-xl border border-white/5 bg-[#0F1115] p-5" data-testid="items-by-hour-top-per-bucket">
    <h4 className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-3">Top items by time slot</h4>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {buckets.map((b) => {
        const rows = topPerBucket[b.label] || [];
        return (
          <div
            key={b.label}
            onMouseEnter={() => onHoverBucket && onHoverBucket(b.label)}
            onMouseLeave={() => onHoverBucket && onHoverBucket(null)}
            className={`rounded-lg border px-3 py-2 transition-colors ${
              hoveredBucket === b.label ? 'bg-white/5 border-white/15' : 'bg-transparent border-white/5'
            }`}
            data-testid={`items-by-hour-bucket-col-${b.label}`}
          >
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm font-semibold text-zinc-100 font-mono">{b.label}</span>
              <span className="text-[10px] text-zinc-500 font-mono">{b.title}</span>
            </div>
            {rows.length === 0 ? (
              <div className="text-[11px] text-zinc-600 font-mono">No items</div>
            ) : (
              <ol className="space-y-1.5">
                {rows.map((r, i) => (
                  <li key={r.item_id} className="flex items-center justify-between gap-2 text-[12px]">
                    <span className="text-zinc-300 truncate flex items-center gap-1.5 min-w-0">
                      <span className="text-zinc-600 font-mono tabular-nums w-3 shrink-0">{i + 1}</span>
                      <span className="truncate" title={r.item_name}>{r.item_name}</span>
                    </span>
                    <span className="font-mono tabular-nums text-zinc-400 shrink-0">{r.qty}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────
// Insight panels
// ──────────────────────────────────────────────────────────────────

const InsightRow = ({ ins, testId }) => {
  const Icon = INSIGHT_ICON[ins.kind] || Lightning;
  const sev = SEVERITY_COLOR[ins.severity] || SEVERITY_COLOR.low;
  return (
    <li className={`flex items-start gap-3 rounded-lg px-3 py-2 border ${sev.bg} ${sev.border}`} data-testid={testId}>
      <Icon size={14} className={`mt-0.5 shrink-0 ${sev.text}`} />
      <div className="flex-1 min-w-0">
        {ins.title && (
          <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-0.5">{ins.title}</div>
        )}
        <p className="text-[12px] text-zinc-200 leading-snug">{ins.message}</p>
      </div>
    </li>
  );
};

const InsightPanel = ({ insights }) => {
  if (!insights?.length) {
    return (
      <div className="rounded-xl border border-white/5 bg-[#0F1115] p-5 flex items-center gap-2" data-testid="items-by-hour-insights-empty">
        <Lightning size={14} className="text-zinc-500" />
        <p className="text-[12px] text-zinc-500 font-mono">
          No strong patterns yet. Widen the window or ingest more days to surface insights.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-white/5 bg-[#0F1115] p-5" data-testid="items-by-hour-insights">
      <div className="flex items-center gap-2 mb-3">
        <Lightning size={14} weight="fill" className="text-[#D4FF00]" />
        <h4 className="text-[10px] uppercase tracking-widest font-mono text-zinc-400">Insights</h4>
      </div>
      <ul className="space-y-2">
        {insights.map((ins, i) => <InsightRow key={i} ins={ins} testId={`items-by-hour-insight-${i}`} />)}
      </ul>
    </div>
  );
};

// Compare-mode insight panel — three fixed slots
const CompareInsightPanel = ({ baseLabel, compareLabel, slots }) => (
  <div className="rounded-xl border border-white/5 bg-[#0F1115] p-5" data-testid="items-by-hour-compare-insights">
    <div className="flex items-center gap-2 mb-3">
      <Lightning size={14} weight="fill" className="text-[#D4FF00]" />
      <h4 className="text-[10px] uppercase tracking-widest font-mono text-zinc-400">
        Comparison insights · {baseLabel} vs {compareLabel}
      </h4>
    </div>
    <ul className="space-y-2">
      {['biggest_difference', 'time_specific_peak', 'consistent_performer'].map((key, i) => {
        const ins = slots[key];
        const label = {
          biggest_difference:   'Biggest difference',
          time_specific_peak:   'Time-specific peak',
          consistent_performer: 'Consistent performer',
        }[key];
        if (!ins) {
          return (
            <li key={key} className="flex items-start gap-3 rounded-lg px-3 py-2 border bg-zinc-500/5 border-zinc-500/20"
                data-testid={`items-by-hour-compare-slot-${key}-empty`}>
              <Equals size={14} className="mt-0.5 shrink-0 text-zinc-600" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-600 mb-0.5">{label}</div>
                <p className="text-[12px] text-zinc-600 font-mono">— no signal in this window</p>
              </div>
            </li>
          );
        }
        return <InsightRow key={key} ins={{ ...ins, title: label }} testId={`items-by-hour-compare-slot-${key}`} />;
      })}
    </ul>
  </div>
);

// ──────────────────────────────────────────────────────────────────
// Control bar
// ──────────────────────────────────────────────────────────────────

const PillButton = ({ active, onClick, children, testId, className = '' }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`text-[11px] font-mono px-3 py-1.5 rounded-md border transition-colors ${
      active
        ? 'bg-white/10 text-zinc-50 border-white/20'
        : 'bg-transparent text-zinc-400 border-white/5 hover:border-white/15'
    } ${className}`}
  >
    {children}
  </button>
);

const SelectWeekday = ({ value, onChange, testId, placeholder = 'Pick day' }) => (
  <select
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    data-testid={testId}
    className="text-[11px] font-mono px-2 py-1.5 rounded-md border bg-[#0F1115] border-white/10 text-zinc-100 hover:border-white/20"
  >
    {value == null && <option value="">{placeholder}</option>}
    {WEEKDAYS.map((d) => (
      <option key={d.idx} value={d.idx}>{d.long}</option>
    ))}
  </select>
);

const ControlBar = ({
  mode, startHour, endHour, onPredefined, onCustom,
  dayFilter, setDayFilter,
  singleDay, setSingleDay,
  baseDay, setBaseDay, compareDay, setCompareDay,
  compareRange, setCompareRange,
  flaggedOnly, setFlaggedOnly,
}) => (
  <div className="space-y-2" data-testid="items-by-hour-control-bar">
    {/* Row 1: Day filter */}
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mr-1">Day</span>
      <PillButton active={dayFilter === 'all'} onClick={() => setDayFilter('all')} testId="items-by-hour-day-all">
        All days
      </PillButton>
      <PillButton active={dayFilter === 'single'} onClick={() => setDayFilter('single')} testId="items-by-hour-day-single">
        Single day
      </PillButton>
      <PillButton active={dayFilter === 'compare'} onClick={() => setDayFilter('compare')} testId="items-by-hour-day-compare">
        Compare days
      </PillButton>

      {dayFilter === 'single' && (
        <div className="flex items-center gap-2 ml-2">
          <SelectWeekday value={singleDay} onChange={setSingleDay} testId="items-by-hour-single-day-select" />
        </div>
      )}

      {dayFilter === 'compare' && (
        <div className="flex items-center gap-2 ml-2 flex-wrap">
          <SelectWeekday value={baseDay} onChange={setBaseDay} testId="items-by-hour-compare-base-select" />
          <span className="text-[11px] text-zinc-500 font-mono">vs</span>
          <SelectWeekday value={compareDay} onChange={setCompareDay} testId="items-by-hour-compare-target-select" />
        </div>
      )}
    </div>

    {/* Row 2: Time mode + (in compare) compare range */}
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mr-1">Time mode</span>
      <PillButton active={mode === 'predefined'} onClick={onPredefined} testId="items-by-hour-mode-predefined">
        Predefined blocks
      </PillButton>
      <PillButton active={mode === 'custom'} onClick={() => onCustom(startHour, endHour)} testId="items-by-hour-mode-custom">
        Custom range
      </PillButton>
      {mode === 'custom' && (
        <div className="flex items-center gap-1 text-[11px] font-mono text-zinc-300">
          <input
            type="number" min={0} max={23} value={startHour}
            onChange={(e) => onCustom(Number(e.target.value), endHour)}
            data-testid="items-by-hour-start-hour"
            className="w-14 bg-white/5 border border-white/10 rounded px-2 py-1 text-center tabular-nums"
          />
          <span className="text-zinc-500">→</span>
          <input
            type="number" min={0} max={23} value={endHour}
            onChange={(e) => onCustom(startHour, Number(e.target.value))}
            data-testid="items-by-hour-end-hour"
            className="w-14 bg-white/5 border border-white/10 rounded px-2 py-1 text-center tabular-nums"
          />
        </div>
      )}

      {dayFilter === 'compare' && (
        <div className="flex items-center gap-2 ml-auto" data-testid="items-by-hour-compare-range">
          <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Compare range</span>
          {COMPARE_RANGES.map((d) => (
            <PillButton
              key={d}
              active={compareRange === d}
              onClick={() => setCompareRange(d)}
              testId={`items-by-hour-compare-range-${d}`}
            >
              Last {d}d
            </PillButton>
          ))}
        </div>
      )}
    </div>

    {/* Row 3: flagged-only toggle */}
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mr-1">Risk</span>
      <PillButton
        active={flaggedOnly}
        onClick={() => setFlaggedOnly(!flaggedOnly)}
        testId="items-by-hour-flagged-only-toggle"
      >
        <span className="inline-flex items-center gap-1">
          <Warning size={11} className={flaggedOnly ? 'text-amber-300' : 'text-zinc-500'} />
          Show only flagged items
        </span>
      </PillButton>
      <span className="text-[10px] text-zinc-600 font-mono">
        surfaces items with high variance, new launches or sparse data
      </span>
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────
// Time-specific peak derivation (client-side, uses qty_per_day)
// ──────────────────────────────────────────────────────────────────

const deriveTimeSpecificPeak = (base, compare, baseLabel, compareLabel) => {
  if (!base?.items?.length && !compare?.items?.length) return null;
  const byNameB = Object.fromEntries((base?.items || []).map((it) => [it.item_name, it]));
  const byNameC = Object.fromEntries((compare?.items || []).map((it) => [it.item_name, it]));
  const names = new Set([...Object.keys(byNameB), ...Object.keys(byNameC)]);

  let best = null;
  for (const name of names) {
    const bCells = Object.fromEntries((byNameB[name]?.cells || []).map((c) => [c.label, c.qty_per_day ?? 0]));
    const cCells = Object.fromEntries((byNameC[name]?.cells || []).map((c) => [c.label, c.qty_per_day ?? 0]));
    const labels = new Set([...Object.keys(bCells), ...Object.keys(cCells)]);
    for (const label of labels) {
      const b = bCells[label] || 0;
      const c = cCells[label] || 0;
      const hi = Math.max(b, c);
      const lo = Math.min(b, c);
      if (hi < 2) continue;
      const delta = hi - lo;
      if (delta < 1) continue;
      const ratio = lo === 0 ? Infinity : hi / lo;
      if (lo > 0 && ratio < 1.5) continue; // not a real peak difference
      if (!best || delta > best.delta) {
        best = { name, label, b, c, delta, ratio, winner: b > c ? baseLabel : compareLabel, loser: b > c ? compareLabel : baseLabel };
      }
    }
  }
  if (!best) return null;
  const ratioStr = !isFinite(best.ratio) ? 'only here' : best.ratio >= 3 ? `${Math.round(best.ratio)}×` : `${best.ratio.toFixed(1)}×`;
  return {
    kind: 'time_specific_peak',
    severity: !isFinite(best.ratio) || best.ratio >= 3 ? 'high' : 'medium',
    message: `${best.name} peaks during ${best.label} on ${best.winner} — ${best.delta.toFixed(1)} more/day than ${best.loser} (${ratioStr}).`,
  };
};

// ──────────────────────────────────────────────────────────────────
// Main view
// ──────────────────────────────────────────────────────────────────

export default function ItemsByHourView({ baseParams }) {
  const [mode, setMode]         = useState('predefined');
  const [startHour, setStartHour] = useState(11);
  const [endHour, setEndHour]     = useState(15);
  const [dayFilter, setDayFilter] = useState('all');          // all | single | compare
  const [singleDay, setSingleDay] = useState(null);
  const [baseDay, setBaseDay]     = useState(6);               // Sunday
  const [compareDay, setCompareDay] = useState(3);             // Thursday
  const [compareRange, setCompareRange] = useState(30);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [hoveredBucket, setHoveredBucket] = useState(null);

  // Auto-pick a sensible single day the first time user switches to 'single'
  useEffect(() => {
    if (dayFilter === 'single' && singleDay == null) setSingleDay(6); // Sunday
  }, [dayFilter, singleDay]);

  const isCompare = dayFilter === 'compare' && baseDay != null && compareDay != null && baseDay !== compareDay;

  // Compare mode may override parent date range with a local preset.
  const compareWindow = useMemo(() => {
    if (!isCompare) return null;
    return { date_from: daysAgoIso(compareRange), date_to: todayIso() };
  }, [isCompare, compareRange]);

  const params = useMemo(() => {
    const p = { ...baseParams, mode, limit_items: 30, day_filter: dayFilter };
    if (mode === 'custom') { p.start_hour = startHour; p.end_hour = endHour; }
    if (dayFilter === 'single' && singleDay != null) p.day = singleDay;
    if (isCompare) {
      p.day = baseDay;
      p.compare_with = compareDay;
      if (compareWindow) {
        p.date_from = compareWindow.date_from;
        p.date_to   = compareWindow.date_to;
      }
    }
    return p;
  }, [baseParams, mode, startHour, endHour, dayFilter, singleDay, isCompare, baseDay, compareDay, compareWindow]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    salesApi.getItemsByHour(params)
      .then((r) => { if (live) setData(r.data); })
      .catch(() => toast.error('Failed to load items-by-hour'))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [JSON.stringify(params)]);

  // ── Derived view models ──
  const comparison = data?.comparison;
  const activeIsCompare = Boolean(comparison?.enabled);

  // Shared maxVal across both arms so the heatmap intensity is comparable.
  const compareMax = useMemo(() => {
    if (!activeIsCompare) return 0;
    let m = 0;
    for (const arm of ['base', 'compare']) {
      for (const it of comparison[arm]?.items || []) {
        for (const c of it.cells) {
          const v = c.qty_per_day ?? 0;
          if (v > m) m = v;
        }
      }
    }
    return m;
  }, [activeIsCompare, comparison]);

  const singleMax = useMemo(() => {
    if (activeIsCompare) return 0;
    let m = 0;
    for (const it of data?.items || []) {
      for (const c of it.cells) if (c.qty > m) m = c.qty;
    }
    return m;
  }, [activeIsCompare, data]);

  // Compare insights → 3 fixed slots
  const compareSlots = useMemo(() => {
    if (!activeIsCompare) return {};
    const backendIns = comparison.insights || [];
    const biggest = backendIns.find((x) => x.kind === 'compare_disparity');
    const consistent = backendIns.find((x) => x.kind === 'compare_consistent');
    const timePeak = deriveTimeSpecificPeak(
      comparison.base, comparison.compare,
      comparison.base?.day, comparison.compare?.day,
    );
    return {
      biggest_difference:   biggest,
      time_specific_peak:   timePeak,
      consistent_performer: consistent,
    };
  }, [activeIsCompare, comparison]);

  return (
    <div className="space-y-4" data-testid="items-by-hour-view">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            <Clock size={14} /> Items by Hour
          </h3>
          <p className="text-[11px] text-zinc-500 font-mono mt-0.5">
            When does each item sell? Feeds prep planning, staffing & upsell timing.
          </p>
        </div>
      </div>

      <ControlBar
        mode={mode}
        startHour={startHour}
        endHour={endHour}
        onPredefined={() => setMode('predefined')}
        onCustom={(s, e) => { setMode('custom'); setStartHour(s); setEndHour(e); }}
        dayFilter={dayFilter}
        setDayFilter={setDayFilter}
        singleDay={singleDay}
        setSingleDay={setSingleDay}
        baseDay={baseDay}
        setBaseDay={setBaseDay}
        compareDay={compareDay}
        setCompareDay={setCompareDay}
        compareRange={compareRange}
        setCompareRange={setCompareRange}
        flaggedOnly={flaggedOnly}
        setFlaggedOnly={setFlaggedOnly}
      />

      {/* Compare mode banner */}
      {activeIsCompare && (
        <div
          className="rounded-xl border border-[#D4FF00]/30 bg-[#D4FF00]/5 px-4 py-3 flex items-center justify-between flex-wrap gap-2"
          data-testid="items-by-hour-compare-banner"
        >
          <div className="flex items-center gap-3">
            <ArrowsLeftRight size={16} weight="fill" className="text-[#D4FF00]" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-[#D4FF00]">Compare mode on</div>
              <div className="text-[13px] text-zinc-100 font-mono">
                {comparison.base?.day} <span className="text-zinc-500">(avg per day)</span>
                {' '}vs{' '}
                {comparison.compare?.day} <span className="text-zinc-500">(avg per day)</span>
              </div>
            </div>
          </div>
          <div className="text-[11px] font-mono text-zinc-400 flex items-center gap-3">
            <span>{data?.period?.date_from} → {data?.period?.date_to}</span>
            <span className="text-zinc-600">·</span>
            <span>{comparison.base?.sample_days} {comparison.base?.day}s · {comparison.compare?.sample_days} {comparison.compare?.day}s</span>
          </div>
        </div>
      )}

      {/* Low-sample warning */}
      {data?.low_sample_warning && (
        <div
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 flex items-center gap-2"
          data-testid="items-by-hour-low-sample-warning"
        >
          <Warning size={14} weight="fill" className="text-amber-300" />
          <p className="text-[12px] text-amber-100 font-mono">
            {activeIsCompare
              ? `Low sample: only ${comparison.base?.sample_days} ${comparison.base?.day}s and ${comparison.compare?.sample_days} ${comparison.compare?.day}s in this window — insights may be noisy.`
              : `Low sample: only ${data.sample_days} matching day${data.sample_days === 1 ? '' : 's'} in this window — insights may be noisy.`}
          </p>
        </div>
      )}

      {loading && !data && (
        <div className="text-[12px] text-zinc-500 font-mono py-12 text-center">Loading hour-level intelligence…</div>
      )}

      {data && !activeIsCompare && (
        <>
          <Heatmap
            buckets={data.buckets}
            items={flaggedOnly ? (data.items || []).filter(hasAnyFlag) : data.items}
            maxVal={singleMax}
            useAvg={false}
            hoveredBucket={hoveredBucket}
            onHoverBucket={setHoveredBucket}
            periodComparison={data.period_comparison}
          />
          {flaggedOnly && (data.items || []).filter(hasAnyFlag).length === 0 && (
            <div
              className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-[12px] text-emerald-300 font-mono"
              data-testid="items-by-hour-no-flagged-items"
            >
              ✓ No flagged items in this window — demand is reliable.
            </div>
          )}
          <InsightPanel insights={data.insights} />
          <TopPerBucket
            buckets={data.buckets}
            topPerBucket={data.top_items_per_bucket || {}}
            hoveredBucket={hoveredBucket}
            onHoverBucket={setHoveredBucket}
          />
        </>
      )}

      {data && activeIsCompare && (
        <>
          <CompareInsightPanel
            baseLabel={comparison.base?.day}
            compareLabel={comparison.compare?.day}
            slots={compareSlots}
          />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="items-by-hour-compare-split">
            <Heatmap
              buckets={data.buckets}
              items={flaggedOnly ? (comparison.base?.items || []).filter(hasAnyFlag) : (comparison.base?.items || [])}
              maxVal={compareMax}
              useAvg
              hoveredBucket={hoveredBucket}
              onHoverBucket={setHoveredBucket}
              label={`${comparison.base?.day} · avg per day (${comparison.base?.sample_days} days)`}
              testId="items-by-hour-heatmap-base"
              periodComparison={data.period_comparison}
            />
            <Heatmap
              buckets={data.buckets}
              items={flaggedOnly ? (comparison.compare?.items || []).filter(hasAnyFlag) : (comparison.compare?.items || [])}
              maxVal={compareMax}
              useAvg
              hoveredBucket={hoveredBucket}
              onHoverBucket={setHoveredBucket}
              label={`${comparison.compare?.day} · avg per day (${comparison.compare?.sample_days} days)`}
              testId="items-by-hour-heatmap-compare"
              periodComparison={data.period_comparison}
            />
          </div>
        </>
      )}
    </div>
  );
}
